import React, { useState, useEffect, useRef } from "react";
import { Stack, Typography } from "@mui/material";
import { HostSelectInput, MeetingHost } from "./inputs";
import { Host, MeetingStatus } from "../types";

interface HostCellProps {
  meetingId: number;
  host: Host | null;
  meetingStatus: MeetingStatus;
  canAssignHost: boolean;
  onHostChange: (meetingId: number, newHost: MeetingHost) => Promise<void>;
}

export function HostCell({
  meetingId,
  host,
  meetingStatus,
  canAssignHost,
  onHostChange,
}: HostCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const isProvisioningCategory = (status: MeetingStatus): boolean => {
    return (
      status === "initializing" ||
      status === "initialized" ||
      status === "init_fault"
    );
  };

  const canEdit = canAssignHost && isProvisioningCategory(meetingStatus);

  const handleHostSelect = async (selectedHost: MeetingHost | null) => {
    if (selectedHost) {
      await onHostChange(meetingId, selectedHost);
    }
    setIsEditing(false);
  };

  // Handle Escape key and click outside to close editing mode
  useEffect(() => {
    if (!isEditing) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsEditing(false);
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsEditing(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isEditing]);

  if (isEditing) {
    return (
      <Stack
        ref={containerRef}
        sx={{ minWidth: 200 }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <HostSelectInput
          host={null}
          onHostSelect={handleHostSelect}
          forMeetingTransfer={false}
          label=""
        />
      </Stack>
    );
  }

  return (
    <Stack
      onClick={(e) => {
        if (canEdit) {
          e.stopPropagation();
          setIsEditing(true);
        }
      }}
      onMouseDown={(e) => {
        if (canEdit) {
          e.stopPropagation();
        }
      }}
      onKeyDown={(e) => {
        if (canEdit && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          e.stopPropagation();
          setIsEditing(true);
        }
      }}
      role={canEdit ? "button" : undefined}
      tabIndex={canEdit ? 0 : undefined}
      sx={{
        cursor: canEdit ? "pointer" : "default",
        padding: 1,
        borderRadius: canEdit ? 1 : 0,
        minHeight: 40,
        justifyContent: "center",
        "&:hover": canEdit ? { backgroundColor: "action.hover" } : {},
      }}
    >
      <Typography variant="caption">
        {host?.firstName} {host?.lastName}
      </Typography>
      <Typography variant="caption">{host?.email}</Typography>
    </Stack>
  );
}
